import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useSingleTimeout } from '../hooks/useTimeout';

interface SliderConfig {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  format: 'currency' | 'percent' | 'number';
  label: string;
  defaultText?: string;
  region?: 'Canada' | 'US';
  colorThresholds?: {
    redLow: number;   // percentage (0-1) for red threshold on low end
    redHigh: number;  // percentage (0-1) for red threshold on high end
    yellowLow: number; // percentage (0-1) for yellow threshold on low end
    yellowHigh: number; // percentage (0-1) for yellow threshold on high end
  };
  guidanceTitle?: string; // if provided, will show a guidance button
  guidanceContent?: string; // if provided, will show a guidance button with the title and content  
  onShowGuidance?: (title: string, content: string) => void; // if provided, will show a guidance button with the title and content  
}

interface InteractiveSliderProps {
  config: SliderConfig;
  value: number;
  onChange: (value: number) => void;
  className?: string;
  guidanceTitle?: string;
  guidanceContent?: string;
  onShowGuidance?: (title: string, content: string) => void;
}

const InteractiveSlider: React.FC<InteractiveSliderProps> = ({ 
  config, 
  value, 
  onChange, 
  className = '',
  guidanceTitle,
  guidanceContent,
  onShowGuidance
}) => {
  const [numericValue, setNumericValue] = useState((value || 0).toString());
  const [dynamicMax, setDynamicMax] = useState(config.max);
  const [dynamicMin, setDynamicMin] = useState(config.min);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const { setTimeout: setSafeTimeout } = useSingleTimeout();

  const formatValue = (val: number) => {
    switch (config.format) {
      case 'currency':
        return new Intl.NumberFormat(config.region === 'Canada' ? 'en-CA' : 'en-US', {
          style: 'currency',
          currency: config.region === 'Canada' ? 'CAD' : 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(val || 0);
      case 'percent':
        return `${(val || 0).toFixed(2)}%`;
      case 'number':
        return new Intl.NumberFormat('en-CA', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          useGrouping: true
        }).format(val || 0);
      default:
        return (val || 0).toString();
    }
  };

  const getSliderColor = (val: number) => {
    const range = dynamicMax - dynamicMin;
    const position = (val - dynamicMin) / range;
    
    // Use custom thresholds if provided, otherwise use defaults
    const redLow = config.colorThresholds?.redLow ?? 0.2;
    const redHigh = config.colorThresholds?.redHigh ?? 0.8;
    const yellowLow = config.colorThresholds?.yellowLow ?? 0.3;
    const yellowHigh = config.colorThresholds?.yellowHigh ?? 0.7;
    
    if (position < redLow || position > redHigh) return '#ef4444'; // Red for extreme
    if (position < yellowLow || position > yellowHigh) return '#f59e0b'; // Yellow for unusual
    return '#10b981'; // Green for typical
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    onChange(newValue);
    setNumericValue(newValue.toString());
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // For number format, allow commas in input but store clean value
    if (config.format === 'number') {
      // Allow digits, commas, and decimal points
      const newValue = e.target.value.replace(/[^\d,.]/g, '');
      setNumericValue(newValue);
    } else {
      // Remove all non-numeric characters except decimal point for other formats
      const newValue = e.target.value.replace(/[^\d.]/g, '');
      setNumericValue(newValue);
    }
    setIsUserTyping(true);
  };

  const handleNumericBlur = () => {
    // Small delay to handle rapid typing scenarios with automatic cleanup
    setSafeTimeout(() => setIsUserTyping(false), 100);
    // Remove commas and other formatting for parsing
    const cleanValue = numericValue.replace(/[^\d.]/g, '');
    const numValue = parseFloat(cleanValue);
    if (!isNaN(numValue)) {
      let clampedValue = numValue;

      // Allow values below the original min, but update the dynamic min if needed
      if (numValue < dynamicMin) {
        setDynamicMin(numValue);
      }

      // Allow values above the original max, but update the dynamic max if needed
      if (numValue > dynamicMax) {
        setDynamicMax(numValue);
      }

      onChange(clampedValue);
      // Update the display value with proper formatting
      const displayValue = config.format === 'number' ? formatValue(clampedValue) : clampedValue.toString();
      setNumericValue(displayValue);
    } else {
      // Reset to formatted value on error
      const displayValue = config.format === 'number' ? formatValue(value || 0) : (value || 0).toString();
      setNumericValue(displayValue);
    }
  };

  const handleNumericKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNumericBlur();
    }
  };

  // Update numericValue when the slider value changes, but only if user is not actively typing
  React.useEffect(() => {
    if (!isUserTyping) {
      // For number format, show formatted value (with commas) in the input field
      const displayValue = config.format === 'number' ? formatValue(value || 0) : (value || 0).toString();
      const currentInput = numericValue;
      
      // Only update if the parent value is actually different from what we're currently showing
      // This prevents the parent from overriding user input that's still being processed
      if (displayValue !== currentInput) {
        setNumericValue(displayValue);
      }
    }
  }, [value, isUserTyping, numericValue, config.format]);

  // Update dynamic min/max when value exceeds current bounds
  React.useEffect(() => {
    if ((value || 0) < dynamicMin) {
      setDynamicMin(value || 0);
    }
    if ((value || 0) > dynamicMax) {
      setDynamicMax(value || 0);
    }
  }, [value, dynamicMin, dynamicMax]);

  return (
    <div className={`interactive-slider ${className}`}>
      <div className="slider-header">
        <div className="label-with-guidance">
          <label className="slider-label">
            <b>{config.label}</b>
            {config.defaultText && <><i dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(config.defaultText, {
              ALLOWED_TAGS: ['br', 'b', 'i', 'strong', 'em', 'span'],
              ALLOWED_ATTR: []
            }) }} /></>}
          </label>
          {guidanceTitle && guidanceContent && onShowGuidance && (
            <button 
              onClick={() => onShowGuidance(guidanceTitle, guidanceContent)}
              className="guidance-btn"
              title={`Get guidance on ${config.label}`}
            >
              ℹ 
            </button>
          )}
        </div>
      </div>
      
      <div className="slider-container">
        <input
          type="range"
          min={dynamicMin}
          max={dynamicMax}
          step={config.step}
          value={Math.max(dynamicMin, Math.min(value || 0, dynamicMax))}
          onChange={handleSliderChange}
          className="slider-input"
          style={{
            '--slider-color': getSliderColor(value || 0)
          } as React.CSSProperties}
          aria-label={config.label}
          aria-valuemin={dynamicMin}
          aria-valuemax={dynamicMax}
          aria-valuenow={value || 0}
          aria-valuetext={formatValue(value || 0)}
        />
        
        <div className="slider-value">
          {formatValue(value || 0)}
        </div>
      </div>

      <div className="numeric-input-container">
        <input
          type="text"
          value={numericValue}
          onChange={handleNumericChange}
          onBlur={handleNumericBlur}
          onKeyPress={handleNumericKeyPress}
          className="numeric-input"
          placeholder={`${dynamicMin} - ${dynamicMax}`}
          id={`numeric-input-${config.label.replace(/\s+/g, '-').toLowerCase()}`}
          aria-label={`Precise ${config.label} input`}
          aria-describedby={`${config.label.replace(/\s+/g, '-').toLowerCase()}-description`}
        />
        <div
          id={`${config.label.replace(/\s+/g, '-').toLowerCase()}-description`}
          className="sr-only"
        >
          Enter a value between {dynamicMin} and {dynamicMax}
        </div>
      </div>
    </div>
  );
};

export default InteractiveSlider; 